import { z } from 'zod';
import { createModel, partySchema, QueryMany, QueryOne } from '../../common';
import { agentPersonaIdSchema } from '../../common/agent.schema';
import { languageSchema } from '../../common/language.schema';
import { dateSchema, idSchema, nameSchema, objectIdSchema } from '../../validation';
import {
  outboundChannelSchema,
  targetMatchSchema,
  targetOriginSchema,
  targetQuerySchema,
  targetSkipReasonSchema,
} from '../targeting/targeting.schema';

export const messageCampaignStatusSchema = z.enum(['draft', 'sending', 'done', 'failed']);
export type MessageCampaignStatus = z.infer<typeof messageCampaignStatusSchema>;

/** What happened on one channel for one person. */
export const messageCampaignDeliverySchema = z.enum([
  'queued',
  'sent',
  'recorded',
  'skipped',
  'failed',
]);
export type MessageCampaignDelivery = z.infer<typeof messageCampaignDeliverySchema>;

/** A label for what the campaign is for — never tied to a channel. */
export const messageCampaignPurposeSchema = z.enum(['outreach', 'announcement', 'follow_up']);
export type MessageCampaignPurpose = z.infer<typeof messageCampaignPurposeSchema>;

/**
 * Where one recipient got to on one channel, furthest step wins.
 *
 * WhatsApp walks `sent → delivered → read → replied` (a reply outranks
 * read receipts — some people turn them off). Push stops at `sent` /
 * `no_devices`; email at `sent`. `failed` = the provider refused;
 * `skipped` = never attempted on this channel.
 */
export const messageCampaignEngagementStatusSchema = z.enum([
  'skipped',
  'failed',
  'no_devices',
  'sent',
  'delivered',
  'read',
  'replied',
]);
export type MessageCampaignEngagementStatus = z.infer<typeof messageCampaignEngagementStatusSchema>;

export const messageCampaignChannelEngagementSchema = z.object({
  status: messageCampaignEngagementStatusSchema,
  sentAt: dateSchema.optional(),
  deliveredAt: dateSchema.optional(),
  readAt: dateSchema.optional(),
  repliedAt: dateSchema.optional(),
  replyCount: z.number().int().nonnegative().optional(),
  /** Why a `failed` delivery failed — the provider's receipt text (e.g. `131049: …`). */
  failureReason: z.string().max(1000).optional(),
});
export type MessageCampaignChannelEngagement = z.infer<typeof messageCampaignChannelEngagementSchema>;

/** Funnel counts for one channel (or the whole campaign). */
export const messageCampaignFunnelSchema = z.object({
  sent: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  replied: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  noDevices: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type MessageCampaignFunnel = z.infer<typeof messageCampaignFunnelSchema>;

/**
 * Engagement snapshot — computed from `ConversationMessage` rows (the
 * source of truth) by `MessageCampaignAnalyticsService` and saved here
 * so lists stay cheap. `computedAt` says how fresh it is.
 *
 * `total` counts people: sent = reached on at least one channel,
 * replied = replied anywhere. `byChannel` counts per channel.
 */
export const messageCampaignStatsSchema = z.object({
  total: messageCampaignFunnelSchema,
  byChannel: z.record(outboundChannelSchema, messageCampaignFunnelSchema),
  /** replied / sent, 0..1 — people, not messages. */
  replyRate: z.number().min(0).max(1),
  computedAt: dateSchema,
});
export type MessageCampaignStats = z.infer<typeof messageCampaignStatsSchema>;

/**
 * The WhatsApp line a campaign went out on — one line for every
 * recipient, so replies, the persona and the lead's brand agree.
 *
 * - `auto` — the template's pinned line (`catalog.whatsapp.line`), else
 *   the default Hero line.
 * - `manual` — staff picked the `ChannelAccount`. Must match the
 *   template's pin when it has one.
 */
export const messageCampaignSenderModeSchema = z.enum(['auto', 'manual']);
export type MessageCampaignSenderMode = z.infer<typeof messageCampaignSenderModeSchema>;

export const messageCampaignSenderSchema = z.object({
  mode: messageCampaignSenderModeSchema,
  channelAccountId: objectIdSchema,
  /** Persona pinned on the account (`wathiqa_plus`); absent = the Hero line. */
  personaId: agentPersonaIdSchema.optional(),
  /** Snapshot for history lists — the account's `title` / `displayId` at send time. */
  title: nameSchema.optional(),
  displayId: nameSchema.optional(),
});
export type MessageCampaignSender = z.infer<typeof messageCampaignSenderSchema>;

export const messageCampaignRecipientOutcomeSchema = z.object({
  key: idSchema,
  phone: idSchema.optional(),
  email: idSchema.optional(),
  userId: objectIdSchema.optional(),
  leadId: objectIdSchema.optional(),
  orgId: objectIdSchema.optional(),
  conversationId: objectIdSchema.optional(),
  name: nameSchema.optional(),
  /** Uploaded sheet's company — seeds the lead created on delivery. */
  companyName: nameSchema.optional(),
  language: languageSchema.optional(),
  origin: targetOriginSchema,
  /** `queued` when at least one channel took it; `skipped` before any send; `failed` when every channel failed. */
  outcome: z.enum(['queued', 'skipped', 'failed']),
  reason: idSchema.optional(),
  /** Per-channel result — absent channels were not requested or not reachable. */
  deliveries: z.record(outboundChannelSchema, messageCampaignDeliverySchema).optional(),
  /** Uploaded contacts only — what the phone matched before the send. */
  match: targetMatchSchema.optional(),
  /**
   * Link or create the lead once WhatsApp confirms delivery. Cleared when
   * that happens; a message that never arrives writes no lead.
   */
  leadOnDelivery: z.boolean().optional(),
  /** True when this run created `leadId` (an outbound first touch), on delivery. */
  leadCreated: z.boolean().optional(),
  /** Per-channel engagement — written by the analytics snapshot. */
  engagement: z.record(outboundChannelSchema, messageCampaignChannelEngagementSchema).optional(),
});
export type MessageCampaignRecipientOutcome = z.infer<typeof messageCampaignRecipientOutcomeSchema>;

/**
 * `MessageCampaign` — one bulk template send, from **target** to outcome.
 *
 * Bulk without an audit row is how a mis-filtered 500-phone blast becomes
 * untraceable. The targeting query is snapshotted (not the resolved
 * list — that is `recipients[]`), so a reviewer sees both what was asked
 * and who it reached, per channel. Every queued WhatsApp row on a
 * recipient's thread carries `outbound.campaignId` back here.
 *
 * It is the only record of who received what — nothing about a campaign
 * is copied onto `Lead` / `User` / `Org`. Reverse lookups ("which
 * campaigns reached this lead") query `recipients.*` here.
 *
 * Indexes (migration `20260928100000-message-campaign-indexes`):
 * `{ 'doc.createdAt': -1 }`, `{ 'recipients.leadId': 1 }`,
 * `{ 'recipients.userId': 1 }`, `{ 'recipients.phone': 1 }`,
 * `{ purpose: 1, 'doc.createdAt': -1 }`.
 */
export const MessageCampaignSchema = createModel({
  /** What this is about, for staff. Required on create; older runs have none. */
  title: nameSchema.optional(),
  description: z.string().max(2000).optional(),
  purpose: messageCampaignPurposeSchema.optional(),
  templateId: idSchema,
  /** Channels requested — a subset of the template's catalog `channels`. */
  channels: z.array(outboundChannelSchema).min(1),
  /** WhatsApp line it went out on. Absent on push/email-only runs and older runs. */
  sender: messageCampaignSenderSchema.optional(),
  /** Fixed language for the batch; absent = each recipient's own. */
  language: languageSchema.optional(),
  /** Variables shared by every recipient (per-recipient ones are built server-side). */
  variables: z.record(z.string()).optional(),
  /** Who — the targeting query as sent. */
  target: targetQuerySchema,
  status: messageCampaignStatusSchema,
  /** Staff member who pressed Send — AdminUser party snapshot. */
  createdBy: partySchema.optional(),
  counts: z.object({
    resolved: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /** Per requested channel: how many recipients it was queued / sent to. */
    byChannel: z.record(outboundChannelSchema, z.number().int().nonnegative()).optional(),
  }),
  excluded: z.record(targetSkipReasonSchema, z.number().int().nonnegative()).optional(),
  recipients: z.array(messageCampaignRecipientOutcomeSchema).default([]),
  /** Engagement snapshot — see {@link messageCampaignStatsSchema}. */
  stats: messageCampaignStatsSchema.optional(),
  startedAt: dateSchema.optional(),
  finishedAt: dateSchema.optional(),
  error: z.string().optional(),
});

export type MessageCampaign = z.infer<typeof MessageCampaignSchema.model>;
export type MessageCampaignData = z.infer<typeof MessageCampaignSchema.data>;
export type MessageCampaignPartial = z.infer<typeof MessageCampaignSchema.partial>;
export type MessageCampaignQueryOne = QueryOne<MessageCampaign>;
export type MessageCampaignQueryMany = QueryMany<MessageCampaign>;
